import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useWindowDimensions } from "react-native";

import ReceivedInvitationsView from "./ReceivedInvitationsView";
import ReceivedInvitationsItem from "@/features/invitations/components/molecules/ReceivedInvitationItem";

import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import {
  InvitationsInvitationFragment,
  InvitationStatus,
  useInvitationsListMyReceivedInvitationsQuery,
} from "@/generated/graphql";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { ifWeb } from "@/utils/platform";

/**
 * NativeWind `md:` breakpoint (768px). The content panel fixes its width at md+
 * and otherwise fills the viewport — a screen-width decision derived from the
 * measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

/**
 * Container component for displaying received invitations.
 * Fetches pending invitations with pagination and refresh support.
 */
const ReceivedInvitationsContainer = () => {
  const { data, fetchMore, loading, refetch, networkStatus } =
    useInvitationsListMyReceivedInvitationsQuery({
      variables: { status: InvitationStatus.Pending },
    });

  const isRefreshing = useMemo(
    () => networkStatus === NetworkStatus.refetch,
    [networkStatus]
  );

  const invitations = useMemo(
    () => data?.listMyReceivedInvitations?.edges.map(edge => edge.node) ?? [],
    [data?.listMyReceivedInvitations?.edges]
  );

  const endCursor = useMemo(
    () =>
      data?.listMyReceivedInvitations?.pageInfo.hasNextPage
        ? data?.listMyReceivedInvitations?.pageInfo.endCursor
        : null,
    [
      data?.listMyReceivedInvitations?.pageInfo.endCursor,
      data?.listMyReceivedInvitations?.pageInfo.hasNextPage,
    ]
  );

  const handleLoadMore = useCallback(() => {
    if (endCursor && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const renderItem: ListRenderItem<InvitationsInvitationFragment> = useCallback(
    ({ item }) => <ReceivedInvitationsItem item={item} />,
    []
  );

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && invitations.length ? (
        <Spinner size="large" color="white" />
      ) : null,
    [loading, isRefreshing, invitations.length]
  );

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? ifWeb(640, 750) : width;

  useSetPageTitle("Pending Invitations");

  return (
    <ReceivedInvitationsView
      invitations={invitations}
      refreshing={isRefreshing}
      widthContent={widthContent}
      renderItem={renderItem}
      renderListFooterComponent={renderListFooterComponent}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
    />
  );
};

export default ReceivedInvitationsContainer;
